/**
 * Job Status Constants and Utilities
 * 
 * These constants define the valid statuses for jobs in the system.
 * NOTE: This is separate from job_technicians.status which has its own status system (1-9).
 */

import {
  getJobStatusColor,
  getJobStatusColorOverrideHex,
  getStatusChipStylesFromHex,
} from './jobStatusColors';

/**
 * Solid chip palette per status — semantic hues, 300-level fills, 600 borders, 900 text.
 * @type {Record<number, { bgColor: string, textColor: string, borderColor: string }>}
 */
export const JOB_STATUS_CHIP_STYLES = {
  1: { bgColor: '#fde047', textColor: '#713f12', borderColor: '#ca8a04' },   // Open — amber (new / needs attention)
  2: { bgColor: '#5eead4', textColor: '#134e4a', borderColor: '#0d9488' },   // Secured — teal (confirmed)
  3: { bgColor: '#86efac', textColor: '#14532d', borderColor: '#16a34a' },   // Job Done — green (complete)
  4: { bgColor: '#93c5fd', textColor: '#1e3a8a', borderColor: '#2563eb' },   // Scheduled — blue (planned)
  5: { bgColor: '#a5b4fc', textColor: '#312e81', borderColor: '#4f46e5' },   // On Site — indigo (active work)
  6: { bgColor: '#fdba74', textColor: '#7c2d12', borderColor: '#ea580c' },   // Return Trip Needed — orange (revisit)
  7: { bgColor: '#d8b4fe', textColor: '#581c87', borderColor: '#9333ea' },   // Parts Needed — purple (blocked)
  8: { bgColor: '#fda4af', textColor: '#881337', borderColor: '#e11d48' },   // Awaiting Approval — rose (client wait)
  9: { bgColor: '#6ee7b7', textColor: '#064e3b', borderColor: '#059669' },   // Approved — emerald (cleared)
  10: { bgColor: '#f0abfc', textColor: '#701a75', borderColor: '#c026d3' },  // Awaiting Advice — fuchsia (internal wait)
  11: { bgColor: '#c4b5fd', textColor: '#4c1d95', borderColor: '#7c3aed' },  // Recall No Charge — violet (warranty)
  12: { bgColor: '#67e8f9', textColor: '#164e63', borderColor: '#0891b2' },  // Ready for Invoicing — cyan (billing)
  13: { bgColor: '#bef264', textColor: '#365314', borderColor: '#65a30d' },  // Invoiced — lime (revenue)
  14: { bgColor: '#fca5a5', textColor: '#7f1d1d', borderColor: '#dc2626' },  // Cancelled — red (stopped)
  15: { bgColor: '#d6c4a8', textColor: '#422006', borderColor: '#92400e' },  // Invoiced/Loss — brown (financial loss)
  16: { bgColor: '#7dd3fc', textColor: '#0c4a6e', borderColor: '#0284c7' },  // Pending Quote — sky (quote stage)
};

const DEFAULT_STATUS_CHIP_STYLE = {
  bgColor: '#d1d5db',
  textColor: '#1f2937',
  borderColor: '#6b7280',
};

/** Accent hex per status (matches borderColor). */
export const JOB_STATUS_HEX_COLORS = Object.fromEntries(
  Object.entries(JOB_STATUS_CHIP_STYLES).map(([id, style]) => [id, style.borderColor])
);

export const JOB_STATUSES = {
  OPEN: { id: 1, label: 'Open', color: 'warning', hex: JOB_STATUS_HEX_COLORS[1] },
  SECURED: { id: 2, label: 'Secured', color: 'info', hex: JOB_STATUS_HEX_COLORS[2] },
  SCHEDULED: { id: 4, label: 'Scheduled', color: 'info', hex: JOB_STATUS_HEX_COLORS[4] },
  ON_SITE: { id: 5, label: 'On Site', color: 'primary', hex: JOB_STATUS_HEX_COLORS[5] },
  RETURN_TRIP_NEEDED: { id: 6, label: 'Return Trip Needed', color: 'warning', hex: JOB_STATUS_HEX_COLORS[6] },
  PARTS_NEEDED: { id: 7, label: 'Parts Needed', color: 'warning', hex: JOB_STATUS_HEX_COLORS[7] },
  PENDING_QUOTA: { id: 16, label: 'Pending Quote', color: 'default', hex: JOB_STATUS_HEX_COLORS[16] },
  AWAITING_ADVICE: { id: 10, label: 'Awaiting Advice', color: 'default', hex: JOB_STATUS_HEX_COLORS[10] },
  AWAITING_APPROVAL: { id: 8, label: 'Awaiting Approval', color: 'default', hex: JOB_STATUS_HEX_COLORS[8] },
  APPROVED: { id: 9, label: 'Approved', color: 'success', hex: JOB_STATUS_HEX_COLORS[9] },
  DONE: { id: 3, label: 'Job Done', color: 'success', hex: JOB_STATUS_HEX_COLORS[3] },
  RECALL_NO_CHARGE: { id: 11, label: 'Recall No Charge', color: 'secondary', hex: JOB_STATUS_HEX_COLORS[11] },
  READY_FOR_INVOICING: { id: 12, label: 'Ready for Invoicing', color: 'info', hex: JOB_STATUS_HEX_COLORS[12] },
  INVOICED: { id: 13, label: 'Invoiced', color: 'success', hex: JOB_STATUS_HEX_COLORS[13] },
  INVOICED_LOSS: { id: 15, label: 'Invoiced/Loss', color: 'error', hex: JOB_STATUS_HEX_COLORS[15] },
  CANCELLED: { id: 14, label: 'Cancelled', color: 'error', hex: JOB_STATUS_HEX_COLORS[14] },
};

export const ARCHIVED_JOB_STATUS_IDS = [
  JOB_STATUSES.DONE.id,
  JOB_STATUSES.RECALL_NO_CHARGE.id,
  JOB_STATUSES.READY_FOR_INVOICING.id,
  JOB_STATUSES.INVOICED.id,
  JOB_STATUSES.CANCELLED.id,
  JOB_STATUSES.INVOICED_LOSS.id,
];

export const PRIORITY_JOB_STATUS_IDS = [
  JOB_STATUSES.OPEN.id,
  JOB_STATUSES.ON_SITE.id,
  JOB_STATUSES.SCHEDULED.id,
  JOB_STATUSES.PENDING_QUOTA.id,
  JOB_STATUSES.AWAITING_APPROVAL.id,
  JOB_STATUSES.APPROVED.id,
];

/** Jobs route `/jobs/job-done`: only status "Job Done" (excludes ready-for-invoicing, invoiced, etc.). */
export const JOB_DONE_VIEW_STATUS_IDS = [JOB_STATUSES.DONE.id];

export const ACTIVE_JOB_STATUS_IDS = Object.values(JOB_STATUSES)
  .map((status) => status.id)
  .filter((statusId) => !ARCHIVED_JOB_STATUS_IDS.includes(statusId));

// Map of status ID to status object for quick lookup
const STATUS_MAP = {
  1: JOB_STATUSES.OPEN,
  2: JOB_STATUSES.SECURED,
  3: JOB_STATUSES.DONE,
  4: JOB_STATUSES.SCHEDULED,
  5: JOB_STATUSES.ON_SITE,
  6: JOB_STATUSES.RETURN_TRIP_NEEDED,
  7: JOB_STATUSES.PARTS_NEEDED,
  8: JOB_STATUSES.AWAITING_APPROVAL,
  9: JOB_STATUSES.APPROVED,
  10: JOB_STATUSES.AWAITING_ADVICE,
  11: JOB_STATUSES.RECALL_NO_CHARGE,
  12: JOB_STATUSES.READY_FOR_INVOICING,
  13: JOB_STATUSES.INVOICED,
  14: JOB_STATUSES.CANCELLED,
  15: JOB_STATUSES.INVOICED_LOSS,
  16: JOB_STATUSES.PENDING_QUOTA
};

/**
 * Get the label for a job status ID
 * @param {number} statusId - The status ID
 * @returns {string} The status label
 */
export const getStatusLabel = (statusId) => {
  const status = STATUS_MAP[statusId];
  return status ? status.label : 'Unknown';
};

/**
 * Get the color for a job status ID
 * @param {number} statusId - The status ID
 * @returns {string} The Material-UI color name
 */
export const getStatusColor = (statusId) => {
  const status = STATUS_MAP[statusId];
  return status ? status.color : 'default';
};

/**
 * Get the unique hex color for a job status ID (override or default accent).
 * @param {number} statusId
 * @returns {string}
 */
export const getStatusHexColor = (statusId) => getJobStatusColor(statusId);

/**
 * Solid chip styles for a job status (saturated fill, strong border, readable text).
 * Uses built-in palette unless the user set a custom color in Settings.
 * @param {number} statusId
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export const getStatusChipStyles = (statusId) => {
  const id = Number(statusId);
  const overrideHex = getJobStatusColorOverrideHex(id);
  if (overrideHex) return getStatusChipStylesFromHex(overrideHex);
  return JOB_STATUS_CHIP_STYLES[id] || DEFAULT_STATUS_CHIP_STYLE;
};

/**
 * MUI sx for status filter chips: outline when idle, solid fill + ring when selected.
 * @param {number} statusId
 * @param {{ selected?: boolean, isDarkMode?: boolean }} options
 */
export const getStatusFilterChipSx = (statusId, { selected = false, isDarkMode = false } = {}) => {
  const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);
  const base = {
    height: '32px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    '& .MuiChip-icon': {
      marginLeft: '6px',
    },
    '& .MuiChip-label': {
      paddingLeft: selected ? '4px' : undefined,
    },
  };

  if (selected) {
    return {
      ...base,
      backgroundColor: borderColor,
      color: '#ffffff',
      border: `2px solid ${textColor}`,
      fontWeight: 700,
      boxShadow: `0 0 0 2px ${isDarkMode ? '#0f172a' : '#ffffff'}, 0 2px 8px rgba(0, 0, 0, 0.22)`,
      '& .MuiChip-icon': { color: '#ffffff !important' },
      '&:hover': {
        backgroundColor: textColor,
        borderColor: textColor,
        boxShadow: `0 0 0 2px ${isDarkMode ? '#0f172a' : '#ffffff'}, 0 3px 10px rgba(0, 0, 0, 0.28)`,
      },
    };
  }

  return {
    ...base,
    backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.55)' : '#ffffff',
    color: borderColor,
    border: `2px solid ${borderColor}`,
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: bgColor,
      borderColor: textColor,
      color: textColor,
    },
  };
};

/**
 * Get all statuses as an array
 * @returns {Array} Array of all status objects with id, label, and color
 */
export const getAllStatuses = () => {
  return Object.values(JOB_STATUSES);
};

/**
 * Get all statuses as an array suitable for Select/Dropdown components
 * @returns {Array} Array of objects with value and label properties
 */
export const getStatusOptions = () => {
  return Object.values(JOB_STATUSES).map(status => ({
    value: status.id,
    label: status.label
  }));
};

/**
 * Get statuses that can be used while creating a job.
 * Jobs cannot start in Invoiced status.
 * @returns {Array<{value:number,label:string}>}
 */
export const getCreatableStatusOptions = () => {
  const excludedStatuses = [
    JOB_STATUSES.INVOICED.id,
    JOB_STATUSES.INVOICED_LOSS.id,
    JOB_STATUSES.CANCELLED.id,
    JOB_STATUSES.PENDING_QUOTA.id,
    JOB_STATUSES.AWAITING_APPROVAL.id,
    JOB_STATUSES.AWAITING_ADVICE.id,
    JOB_STATUSES.RECALL_NO_CHARGE.id,
    JOB_STATUSES.APPROVED.id,
    JOB_STATUSES.DONE.id,
    JOB_STATUSES.READY_FOR_INVOICING.id,
  ];
  return getStatusOptions().filter((status) => !excludedStatuses.includes(status.value));
};

/**
 * Whether status can be moved to Invoiced based on proposals.
 * @param {Array<{invoiced?: boolean}>} proposals
 * @returns {boolean}
 */
export const canSetJobStatusToInvoiced = (proposals = []) => {
  if (!Array.isArray(proposals) || proposals.length === 0) return false;
  return proposals.some((proposal) => proposal?.invoiced === true);
};

/**
 * Get status groups for organized dropdown display
 * @returns {Array} Array of grouped statuses
 */
export const getStatusGroups = () => {
  return [
    {
      label: 'In Progress',
      statuses: [
        JOB_STATUSES.OPEN,
        JOB_STATUSES.SECURED,
        JOB_STATUSES.PENDING_QUOTA,
        JOB_STATUSES.SCHEDULED,
        JOB_STATUSES.ON_SITE
      ]
    },
    {
      label: 'Issues',
      statuses: [
        JOB_STATUSES.RETURN_TRIP_NEEDED,
        JOB_STATUSES.PARTS_NEEDED,
        JOB_STATUSES.AWAITING_APPROVAL,
        JOB_STATUSES.AWAITING_ADVICE,
        JOB_STATUSES.RECALL_NO_CHARGE
      ]
    },
    {
      label: 'Completion',
      statuses: [
        JOB_STATUSES.APPROVED,
        JOB_STATUSES.DONE,
        JOB_STATUSES.READY_FOR_INVOICING,
        JOB_STATUSES.INVOICED
      ]
    },
    {
      label: 'Other',
      statuses: [
        JOB_STATUSES.CANCELLED,
        JOB_STATUSES.INVOICED_LOSS
      ]
    }
  ];
};

/**
 * Check if a status is considered "active" (job in progress)
 * @param {number} statusId - The status ID
 * @returns {boolean} True if status is active
 */
export const isActiveStatus = (statusId) => {
  return [1, 2, 4, 5, 6, 7, 8, 10, 11, 16].includes(statusId);
};

/**
 * Check if a status is considered "completed"
 * @param {number} statusId - The status ID
 * @returns {boolean} True if status is completed
 */
export const isCompletedStatus = (statusId) => {
  return [3, 9, 12, 13, 15].includes(statusId);
};

/**
 * Check if a status is considered "cancelled"
 * @param {number} statusId - The status ID
 * @returns {boolean} True if status is cancelled
 */
export const isCancelledStatus = (statusId) => {
  return statusId === 14;
};

/**
 * Statuses that zero the NTE when selected (Cancelled, Recall No Charge).
 */
export const isNteZeroingStatus = (statusId) =>
  [JOB_STATUSES.CANCELLED.id, JOB_STATUSES.RECALL_NO_CHARGE.id].includes(Number(statusId));

/**
 * Check if a status is Invoiced or Invoiced/Loss.
 * @param {number} statusId - The status ID
 * @returns {boolean}
 */
export const isInvoicedStatus = (statusId) => {
  const status = Number(statusId);
  return status === JOB_STATUSES.INVOICED.id || status === JOB_STATUSES.INVOICED_LOSS.id;
};

/**
 * Statuses that auto-lock a job (Recall No Charge, Invoiced, Cancelled,
 * Invoiced/Loss). Mirrors the backend constant
 * in contracting-api/src/utils/jobStatuses.js.
 */
export const LOCKED_JOB_STATUSES = [11, 13, 14, 15];

/**
 * Whether the given status will (or did) trigger an auto-lock.
 * @param {number} statusId
 * @returns {boolean}
 */
export const isLockingStatus = (statusId) =>
  LOCKED_JOB_STATUSES.includes(Number(statusId));
